import { mdiPencilOutline } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import MultiSelect from "components/MultiSelect";
import Select from "components/Select";
import type { IOption } from "models/Form";

import { imageEditorControlClass } from "./controlStyles";

interface ImageDetailsStageProps {
  file: { id: string; name: string; preview: string; category?: IOption[] };
  categories: IOption[];
  visibleTo?: IOption;
  visibilityOptions: IOption[];
  canEditVisibility: boolean;
  disableCategorySelection: boolean;
  isUploading: boolean;
  uploadProgress: number;
  isCompressing: boolean;
  compressionProgress: number;
  onCategoryChange: (category: IOption[]) => void;
  onVisibilityChange: (visibility: IOption) => void;
  onEdit: () => void;
  onSubmit?: () => Promise<unknown>;
}

const getProgressLabel = (
  isCompressing: boolean,
  compressionProgress: number,
  isUploading: boolean,
  uploadProgress: number
) => {
  if (isCompressing) return `Preparing photo… ${Math.round(compressionProgress)}%`;
  if (isUploading) return `Submitting… ${Math.round(uploadProgress)}%`;
  return "Submit photo";
};

export function ImageDetailsStage({
  file,
  categories,
  visibleTo,
  visibilityOptions,
  canEditVisibility,
  disableCategorySelection,
  isUploading,
  uploadProgress,
  isCompressing,
  compressionProgress,
  onCategoryChange,
  onVisibilityChange,
  onEdit,
  onSubmit
}: Readonly<ImageDetailsStageProps>) {
  const hasCategory = disableCategorySelection || Boolean(file.category?.length);
  const hasVisibility = !canEditVisibility || Boolean(visibleTo);
  const isBusy = isUploading || isCompressing;

  const progressLabel = getProgressLabel(
    isCompressing,
    compressionProgress,
    isUploading,
    uploadProgress
  );
  const categoryControlId = `image-category-${file.id}`;
  const visibilityControlId = `image-visibility-${file.id}`;

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-foreground px-4 pb-4 pt-4 text-text desktop:min-h-[38rem] desktop:p-6">
      <div className="mb-4 min-w-0 pr-12">
        <p className="truncate text-sm text-placeholder">{file.name}</p>
      </div>

      <div className="relative mb-5 flex min-h-[16rem] flex-1 items-center justify-center overflow-hidden rounded-xl bg-black/90 desktop:min-h-[24rem]">
        <img
          src={file.preview}
          alt={`Preview of ${file.name}`}
          className="max-h-full max-w-full object-contain"
        />
        <button
          type="button"
          onClick={onEdit}
          className={`absolute right-3 top-3 gap-2 ${imageEditorControlClass}`}
          aria-label="Edit photo framing">
          <Icon path={mdiPencilOutline} className="size-4" />
          <span className="text-sm font-medium">Edit</span>
        </button>
      </div>

      <div className="grid min-w-0 gap-4 desktop:grid-cols-2">
        {!disableCategorySelection && (
          <div className="min-w-0">
            <label htmlFor={categoryControlId} className="mb-2 block text-sm font-medium">
              Category
            </label>
            <MultiSelect
              id={categoryControlId}
              options={categories}
              value={file.category || []}
              onChange={(value) => onCategoryChange(value as IOption[])}
              placeholder="Select category"
              className="min-w-0 w-full"
              btnClass="!h-12 !min-w-0 !w-full !overflow-hidden !bg-background !px-4 !py-3 !text-base"
            />
          </div>
        )}

        {canEditVisibility && (
          <div>
            <label htmlFor={visibilityControlId} className="mb-2 block text-sm font-medium">
              Visible To
            </label>
            <Select
              id={visibilityControlId}
              options={visibilityOptions}
              value={visibleTo}
              onChange={onVisibilityChange}
              placeholder="Select visibility"
              className="w-full"
              btnClass="!h-12 !w-full !bg-background !px-4 !py-3 !text-base"
            />
          </div>
        )}
      </div>

      {onSubmit && (
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!hasCategory || !hasVisibility || isBusy}
          className="relative mt-6 !h-12 w-full overflow-hidden text-base">
          <span className="relative z-10">{progressLabel}</span>
          {isBusy && (
            <span
              className="absolute inset-y-0 left-0 bg-white/20 transition-[width]"
              style={{ width: `${isCompressing ? compressionProgress : uploadProgress}%` }}
            />
          )}
        </Button>
      )}
    </div>
  );
}
